import { logger } from '../utils/logger'

/**
 * Manages LLM usage tracking and prevents duplicate queries
 * Future: Can be extended to support premium subscriptions
 */
export class LLMUsageManager {
  private static instance: LLMUsageManager
  private usageTracker: Map<string, boolean> = new Map()
  private projectId: string | null = null

  private constructor() {
    logger.debug('LLMUsageManager initialized')
  }

  public static getInstance(): LLMUsageManager {
    if (!LLMUsageManager.instance) {
      LLMUsageManager.instance = new LLMUsageManager()
    }
    return LLMUsageManager.instance
  }

  /**
   * Set the current project ID for usage tracking
   */
  public setProjectId(projectId: string): void {
    this.projectId = projectId
    logger.debug('LLM usage tracking set for project:', projectId)
  }

  /**
   * Check if an LLM operation has already been performed for this project
   */
  public hasUsedLLM(operation: string): boolean {
    if (!this.projectId) return false
    
    const key = `${this.projectId}_${operation}`
    return this.usageTracker.has(key)
  }

  /**
   * Mark an LLM operation as used for this project
   */
  public markLLMUsed(operation: string): void {
    if (!this.projectId) return
    
    const key = `${this.projectId}_${operation}`
    this.usageTracker.set(key, true)
    logger.debug('LLM operation marked as used:', operation, 'for project:', this.projectId)
  }

  /**
   * Reset usage tracking for a project (useful when switching projects)
   */
  public resetProjectUsage(): void {
    if (!this.projectId) return
    
    // Remove all entries for this project
    const keysToDelete = Array.from(this.usageTracker.keys()).filter(key => 
      key.startsWith(`${this.projectId}_`)
    )
    
    keysToDelete.forEach(key => this.usageTracker.delete(key))
    logger.debug('LLM usage tracking reset for project:', this.projectId)
  }

  /**
   * Check if user can perform LLM operation (for future premium features)
   */
  public canUseLLM(operation: string): boolean {
    // For now, allow one use per operation per project
    // Future: Check premium subscription status
    return !this.hasUsedLLM(operation)
  }

  /**
   * Get usage statistics (for future premium features)
   */
  public getUsageStats(): { [operation: string]: boolean } {
    if (!this.projectId) return {}
    
    const stats: { [operation: string]: boolean } = {}
    const keys = Array.from(this.usageTracker.keys()).filter(key => 
      key.startsWith(`${this.projectId}_`)
    )
    
    keys.forEach(key => {
      const operation = key.replace(`${this.projectId}_`, '')
      stats[operation] = true
    })
    
    return stats
  }
}

// Export singleton instance
export const llmUsageManager = LLMUsageManager.getInstance()
